import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { PaymentSavedDto } from '@tiffinify/contracts';
import { formatDay } from '@tiffinify/core';
import { PageLoader, BtnPrimary, Card, Empty, ErrorText, Field, Helper, Hero, Icon, Mono, RingAvatar, Segmented, Skel, TaskTitle, ToggleRow, TopBar, LoadError } from '../components/ui';
import { post, isNotFound } from '../lib/api';
import { endLabel, errorMessage, formatInr, mealsLabel } from '../lib/format';
import { useBrief, useMe } from '../lib/queries';

export function Payment() {
  const { subId = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const today = me.data?.today ?? '';
  const q = useBrief(subId);
  const b = q.data;
  const returnTo = me.data?.user.role === 'owner' ? '/revenue' : (b ? `/s/${b.subscriberId}` : '/');
  // Untouched fields show what is due and today, from the first frame: no fill-in after render, no jump.
  const [typedAmount, setTypedAmount] = useState<string | null>(null);
  const [method, setMethod] = useState<'upi' | 'cash'>('upi');
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [receipt, setReceipt] = useState(true);
  const amount = typedAmount ?? (b ? String(b.due) : '');
  const date = pickedDate ?? today;

  const n = Number(amount.replace(/[^\d]/g, ''));
  const save = useMutation({
    mutationFn: () => post<PaymentSavedDto>(`/subscriptions/${subId}/payments`, { amount: n, method, paidOn: date }),
    onSuccess: (res) => {
      void qc.invalidateQueries();
      const first = b!.name.split(' ')[0] ?? b!.name;
      nav(`/s/${b!.subscriberId}`, {
        state: { flash: { text: res.state.due === 0 ? `${formatInr(res.payment.amount)} saved. ${first} is paid up.` : `${formatInr(res.payment.amount)} saved. ${formatInr(res.state.due)} still due.`, ...(receipt ? { wa: { label: `Receipt to ${first}`, link: res.receipt.link } } : {}) } },
      });
    },
  });

  if (q.isPending) return <main className="main task"><TopBar to={returnTo} kind="close" label="Close" /><TaskTitle title="Record payment" sub={<Skel w={160} />} /><PageLoader label="Adding up payments" /></main>;
  if (q.isError && !isNotFound(q.error)) return <main className="main task"><TopBar to={returnTo} kind="close" label="Close" /><LoadError what="this plan" onRetry={() => void q.refetch()} /></main>;
  if (!b) return <main className="main task"><TopBar to={returnTo} kind="close" label="Close" /><Empty title="Plan not found" /></main>;

  const balance = Math.max(0, b.due - n);
  const over = n > b.due;

  return (
    <main className="main task">
      <TopBar to={returnTo} kind="close" label="Close" />
      <TaskTitle title="Record payment" sub={`${b.name} · ${mealsLabel(b.slot)}`} />

      <Card pad="sm">
        <div className="pay-who">
          <RingAvatar name={b.name} frac={b.price ? b.paid / b.price : 0} color="var(--due-ring)" />
          <div className="pay-who-text">
            <b className={`pay-who-due ${b.due > 0 ? 'st-due' : 'st-ok'}`}>{b.due > 0 ? `${formatInr(b.due)} due` : 'Paid up'}</b>
            <span className="sub pay-who-meta">{formatInr(b.paid)} net paid of {formatInr(b.price)} · {b.state === 'closed' ? 'Closed' : b.state === 'ended' ? 'Ended' : endLabel(b.daysToEnd, b.endDate)}</span>
          </div>
        </div>
      </Card>

      <Field label="Amount received" id="p-amount" value={amount} onChange={(v) => setTypedAmount(v.replace(/[^\d]/g, ''))} prefix="₹" inputMode="numeric" error={over ? `Only ${formatInr(b.due)} is due` : undefined} />
      <div className="stack"><span className="label">Paid by</span><Segmented label="Paid by" value={method} onChange={setMethod} options={[{ value: 'upi', label: 'UPI' }, { value: 'cash', label: 'Cash' }]} /></div>
      <Field label="Date" id="p-date" type="date" value={date} onChange={setPickedDate} max={today || undefined} />
      {b.due === 0 && <Helper icon="info">Nothing is due on this plan, so there is nothing to record.</Helper>}

      <Hero label="Balance after this payment" gap={12}>
        <Mono muted>After this payment</Mono>
        <div className="pay-balance-row">
          <span className="pay-balance">{formatInr(over ? 0 : balance)} balance</span>
          {!over && n > 0 && balance === 0 && <span className="pay-cleared"><Icon name="check" size={12} sw={3} />Cleared</span>}
        </div>
        <p>{balance === 0 && n > 0 ? `The ${formatInr(b.price)} plan is fully paid. ${b.name.split(' ')[0]} leaves the payments-due list.` : `Ends ${formatDay(b.endDate)}. The rest stays on the due list.`}</p>
      </Hero>

      <Card className="toggle-card"><ToggleRow title={`Send a receipt to ${b.name.split(' ')[0]}`} sub="On WhatsApp, with the amount and balance" on={receipt} onChange={setReceipt} /></Card>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <div className="cta-end"><BtnPrimary busy={save.isPending} label={save.isPending ? 'Saving…' : 'Save payment'} icon="check" onClick={() => save.mutate()} disabled={save.isPending || n < 1 || over || !date || b.due === 0} /></div>
    </main>
  );
}
